import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { theme as appTheme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DEFAULT_BUZZBRIDGE_LOYALTY } from '../../constants/buzzbridgeLoyaltyDefaults';
import type { IGlobalConfigurationUpdate } from '../../redux/configuration/types';
import type { IBuzzbridgeLoyaltyConfig } from '../../types/global/buzzbridgeLoyalty.types';

export function enrichBuzzbridgeLoyaltyForEditor(
    source?: Partial<IBuzzbridgeLoyaltyConfig> | null,
): IBuzzbridgeLoyaltyConfig {
    const base = DEFAULT_BUZZBRIDGE_LOYALTY;
    const src = source ?? {};
    return {
        ...base,
        ...src,
        subscription_commission: { ...base.subscription_commission, ...src.subscription_commission },
        job_service_fee_commission: { ...base.job_service_fee_commission, ...src.job_service_fee_commission },
        redemption_programs:
            src.redemption_programs && src.redemption_programs.length > 0
                ? src.redemption_programs
                : base.redemption_programs,
    };
}

const fieldSx = (themeStyle: keyof typeof appTheme) => ({
    '& .MuiOutlinedInput-root': {
        color: appTheme[themeStyle].tag_white,
        '& fieldset': { borderColor: appTheme[themeStyle].form_divider },
        '&:hover fieldset': { borderColor: appTheme[themeStyle].highlight },
        '&.Mui-focused fieldset': { borderColor: appTheme[themeStyle].highlight },
    },
    '& .MuiInputLabel-root': {
        color: appTheme[themeStyle].form_label,
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: appTheme[themeStyle].form_label,
    },
    '& .MuiInputBase-input::placeholder': {
        color: appTheme[themeStyle].placeholderGray,
        opacity: 1,
    },
    '& .MuiFormHelperText-root': {
        color: appTheme[themeStyle].form_label,
    },
});

interface Props {
    config: IGlobalConfigurationUpdate;
    setConfig: React.Dispatch<React.SetStateAction<IGlobalConfigurationUpdate>>;
    setHasChanges: (v: boolean) => void;
    themeStyle: keyof typeof appTheme;
}

export function GlobalConfigurationBuzzbridgeLoyaltyEditor({ config, setConfig, setHasChanges, themeStyle }: Props) {
    const loyalty = enrichBuzzbridgeLoyaltyForEditor(config.buzzbridge_loyalty);

    const patchLoyalty = (next: Partial<IBuzzbridgeLoyaltyConfig>) => {
        setHasChanges(true);
        setConfig(prev => ({
            ...prev,
            buzzbridge_loyalty: { ...enrichBuzzbridgeLoyaltyForEditor(prev.buzzbridge_loyalty), ...next },
        }));
    };

    return (
        <Accordion
            sx={{
                backgroundColor: 'transparent',
                color: appTheme[themeStyle].tag_white,
                '& .MuiAccordionSummary-root': { color: appTheme[themeStyle].tag_white },
                '& .MuiAccordionDetails-root': { color: appTheme[themeStyle].tag_white },
                '& .MuiFormControlLabel-label': { color: appTheme[themeStyle].tag_white },
            }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>BuzzBridge loyalty & referral</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={loyalty.enabled}
                                onChange={e => patchLoyalty({ enabled: e.target.checked })}
                            />
                        }
                        label="Program enabled"
                    />
                    <TextField
                        label="Signup referral points"
                        type="number"
                        value={loyalty.referral_signup_points}
                        onChange={e => patchLoyalty({ referral_signup_points: Number(e.target.value) || 0 })}
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <TextField
                        label="Subscribe referral points"
                        type="number"
                        value={loyalty.referral_subscribe_points}
                        onChange={e => patchLoyalty({ referral_subscribe_points: Number(e.target.value) || 0 })}
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <Typography sx={{ fontWeight: 700, mt: 1 }}>Subscription commission</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={loyalty.subscription_commission.enabled}
                                onChange={e =>
                                    patchLoyalty({
                                        subscription_commission: {
                                            ...loyalty.subscription_commission,
                                            enabled: e.target.checked,
                                        },
                                    })
                                }
                            />
                        }
                        label="Enabled"
                    />
                    <TextField
                        label="Percentage"
                        type="number"
                        value={loyalty.subscription_commission.percentage}
                        onChange={e =>
                            patchLoyalty({
                                subscription_commission: {
                                    ...loyalty.subscription_commission,
                                    percentage: Number(e.target.value) || 0,
                                },
                            })
                        }
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <TextField
                        label="Cap (USD cents)"
                        type="number"
                        value={loyalty.subscription_commission.cap_usd_cents}
                        onChange={e =>
                            patchLoyalty({
                                subscription_commission: {
                                    ...loyalty.subscription_commission,
                                    cap_usd_cents: Number(e.target.value) || 0,
                                },
                            })
                        }
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <TextField
                        label="Window (months)"
                        type="number"
                        value={loyalty.subscription_commission.window_months}
                        onChange={e =>
                            patchLoyalty({
                                subscription_commission: {
                                    ...loyalty.subscription_commission,
                                    window_months: Number(e.target.value) || 0,
                                },
                            })
                        }
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <Typography sx={{ fontWeight: 700, mt: 1 }}>Job service-fee commission</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={loyalty.job_service_fee_commission.enabled}
                                onChange={e =>
                                    patchLoyalty({
                                        job_service_fee_commission: {
                                            ...loyalty.job_service_fee_commission,
                                            enabled: e.target.checked,
                                        },
                                    })
                                }
                            />
                        }
                        label="Enabled"
                    />
                    <TextField
                        label="Share of platform fee (%)"
                        type="number"
                        value={loyalty.job_service_fee_commission.share_of_platform_fee_percentage}
                        onChange={e =>
                            patchLoyalty({
                                job_service_fee_commission: {
                                    ...loyalty.job_service_fee_commission,
                                    share_of_platform_fee_percentage: Number(e.target.value) || 0,
                                },
                            })
                        }
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <TextField
                        label="Min withdrawal (USD cents)"
                        type="number"
                        value={loyalty.min_withdrawal_usd_cents}
                        onChange={e => patchLoyalty({ min_withdrawal_usd_cents: Number(e.target.value) || 0 })}
                        sx={fieldSx(themeStyle)}
                        size="small"
                    />
                    <TextField
                        label="Agreement PDF URL"
                        value={loyalty.agreement_pdf_url ?? ''}
                        onChange={e => patchLoyalty({ agreement_pdf_url: e.target.value })}
                        placeholder="https://cdn.example.com/referral-agreement.pdf"
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx(themeStyle)}
                        size="small"
                        fullWidth
                    />
                    <Typography sx={{ fontWeight: 700 }}>Redemption programs (catalog)</Typography>
                    {loyalty.redemption_programs.map((prog, idx) => (
                        <Box
                            key={prog.id || idx}
                            sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{prog.id}</Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const next = loyalty.redemption_programs.filter((_, i) => i !== idx);
                                        patchLoyalty({ redemption_programs: next });
                                    }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <TextField
                                label="Label"
                                value={prog.label}
                                onChange={e => {
                                    const next = [...loyalty.redemption_programs];
                                    next[idx] = { ...prog, label: e.target.value };
                                    patchLoyalty({ redemption_programs: next });
                                }}
                                sx={fieldSx(themeStyle)}
                                size="small"
                                fullWidth
                                margin="dense"
                            />
                            <TextField
                                label="Points required"
                                type="number"
                                value={prog.points_required}
                                onChange={e => {
                                    const next = [...loyalty.redemption_programs];
                                    next[idx] = { ...prog, points_required: Number(e.target.value) || 0 };
                                    patchLoyalty({ redemption_programs: next });
                                }}
                                sx={fieldSx(themeStyle)}
                                size="small"
                                fullWidth
                                margin="dense"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={prog.active}
                                        onChange={e => {
                                            const next = [...loyalty.redemption_programs];
                                            next[idx] = { ...prog, active: e.target.checked };
                                            patchLoyalty({ redemption_programs: next });
                                        }}
                                    />
                                }
                                label="Active in catalog"
                            />
                        </Box>
                    ))}
                    <Box>
                        <IconButton
                            onClick={() =>
                                patchLoyalty({
                                    redemption_programs: [
                                        ...loyalty.redemption_programs,
                                        {
                                            id: `program_${Date.now()}`,
                                            label: 'New program',
                                            points_required: 1000,
                                            fulfillment_type: 'email_voucher',
                                            active: false,
                                        },
                                    ],
                                })
                            }>
                            <AddIcon />
                        </IconButton>
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
